import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { usePrepareDelete, useConfirmDelete } from "../../hooks/useDeleteProject";
import { errorMessage } from "../../hooks/useServers";

function megabytes(bytes) {
  return (bytes / (1024 * 1024)).toFixed(1);
}

export function DeleteProjectDialog({ open, onOpenChange, project, onDeleted }) {
  const projectId = project?.id;
  const [step, setStep] = useState("summary"); // summary → confirm → done
  const [prepared, setPrepared] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const deleting = useRef(false); // anti doble clic (además del candado atómico del servidor)

  const prepare = usePrepareDelete(projectId);
  const confirm = useConfirmDelete(projectId);

  const reset = () => {
    setStep("summary");
    setPrepared(null);
    setTypedName("");
    setError("");
    setResult(null);
  };

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    reset();
    prepare
      .mutateAsync()
      .then((data) => !cancelled && setPrepared(data))
      .catch((err) => !cancelled && setError(errorMessage(err, "No se pudo preparar la eliminación.")));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const handleOpenChange = (next) => {
    if (!next && step === "done") onDeleted?.();
    if (!next) reset();
    onOpenChange(next);
  };

  const handleDelete = async () => {
    if (deleting.current || !prepared || typedName.trim() !== project.name) return;
    deleting.current = true;
    setError("");
    try {
      const data = await confirm.mutateAsync({ token: prepared.token, confirmName: typedName });
      setResult(data);
      setStep("done");
    } catch (err) {
      setError(errorMessage(err, "No se pudo eliminar el proyecto."));
    } finally {
      deleting.current = false;
    }
  };

  const summary = prepared?.summary;
  const nameMatches = typedName.trim() === project?.name;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="delete-project-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Eliminar proyecto «{project?.name}»</DialogTitle>
          <DialogDescription>
            {summary?.taller ? (
              <>
                Se elimina del Orquestador y de su carpeta en el taller. <strong>No se toca Drive</strong> ni los servidores de clientes.
              </>
            ) : (
              <>
                Se elimina del Orquestador. <strong>No se toca Drive</strong> ni los servidores reales.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {step !== "done" && !summary && !error && (
          <p className="text-sm text-muted-foreground">Calculando lo que se eliminaría...</p>
        )}

        {step === "summary" && summary && (
          <div className="space-y-3">
            <div className="rounded-lg border border-border p-3 text-sm space-y-1">
              <p className="font-medium">Se eliminará del Orquestador:</p>
              <ul className="list-disc pl-5 text-muted-foreground">
                <li>{summary.conversations} chats y temas ({summary.messages} mensajes)</li>
                <li>
                  {summary.attachments} adjuntos ({megabytes(summary.attachments_bytes)} MB)
                </li>
                <li>
                  Servidores conectados con su contraseña guardada:{" "}
                  {summary.servers.length ? summary.servers.join(", ") : "ninguno"}
                </li>
                {summary.taller && (
                  <li data-testid="delete-taller-line">
                    Su carpeta y su usuario en el taller ({summary.taller.folder}): se borran después de guardar una copia cifrada
                  </li>
                )}
              </ul>
            </div>
            <div className="rounded-lg border border-border p-3 text-sm space-y-1">
              <p className="font-medium">NO se toca:</p>
              <ul className="list-disc pl-5 text-muted-foreground">
                <li>La carpeta de Drive{summary.drive_folder ? ` «${summary.drive_folder}»` : ""}</li>
                <li>{summary.taller ? "Los servidores de clientes (solo se olvida la conexión)" : "Los servidores reales (solo se olvida la conexión)"}</li>
                <li>El registro de auditoría de comandos ({summary.audit_entries_kept} registros se conservan)</li>
              </ul>
            </div>
            <p className="text-xs text-muted-foreground">
              Antes de borrar se guarda una copia cifrada y verificada, para poder restaurarlo si hiciera falta.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancelar
              </Button>
              <Button data-testid="delete-continue-button" variant="destructive" onClick={() => setStep("confirm")}>
                Continuar
              </Button>
            </div>
          </div>
        )}

        {step === "confirm" && summary && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-sm">
                Para confirmar, escribe el nombre exacto del proyecto: <strong>{project.name}</strong>
              </Label>
              <Input
                data-testid="delete-name-input"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder={project.name}
                autoComplete="off"
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={confirm.isPending}>
                Cancelar
              </Button>
              <Button
                data-testid="delete-confirm-button"
                variant="destructive"
                onClick={handleDelete}
                disabled={!nameMatches || confirm.isPending}
              >
                {confirm.isPending ? "Eliminando..." : "Eliminar definitivamente"}
              </Button>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-3">
            <p data-testid="delete-done" className="text-sm text-emerald-500">
              ✅ Proyecto eliminado del Orquestador.
            </p>
            <p className="text-xs text-muted-foreground">
              Copia cifrada guardada: {result.backup_file}. La carpeta de Drive sigue intacta.
            </p>
            {result.taller?.status === "removed" && (
              <p data-testid="delete-taller-done" className="text-xs text-muted-foreground">
                Su carpeta y usuario del taller se borraron{result.taller.copy ? ` (copia cifrada: ${result.taller.copy})` : ""}.
              </p>
            )}
            {result.taller?.status === "pending" && (
              <p data-testid="delete-taller-pending" className="text-sm text-amber-500">
                ⚠️ {result.taller.message}
              </p>
            )}
            <div className="flex justify-end">
              <Button onClick={() => handleOpenChange(false)}>Listo</Button>
            </div>
          </div>
        )}

        {step === "summary" && error && <p className="text-sm text-red-500">{error}</p>}
      </DialogContent>
    </Dialog>
  );
}
